import type { ReactNode } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  jsonLd?: Record<string, unknown>;
  children?: ReactNode;
}

const SITE_URL = (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env?.VITE_SITE_URL as string) || '';
const BASE_PATH = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/') as string;
const pathPrefix = BASE_PATH && BASE_PATH !== '/' ? `${BASE_PATH}` : '';

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogImage,
  ogType = 'website',
  jsonLd,
  children,
}: SEOHeadProps) {
  const fullTitle = title.includes('得運智科') ? title : `${title} | 得運智科股份有限公司`;
  const canonicalUrl = SITE_URL ? `${SITE_URL}${pathPrefix}${canonicalPath}` : undefined;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="zh_TW" />
      <meta property="og:site_name" content="得運智科股份有限公司" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}

      {children}
    </>
  );
}